import { useEffect, useMemo, useRef, useState } from "react";
import {
  FileText,
  Plus,
  Search,
  Sparkles,
  Trash2,
  Edit3,
  ExternalLink,
  Building2,
  Package,
  Users,
  Tag,
  Loader2,
  Upload,
  X,
  Check,
} from "lucide-react";
import api, { API, formatErr } from "../../lib/api";
import Modal from "../Modal";
import { toast } from "sonner";
import VoiceTextarea from "../VoiceTextarea";
import { useBranding } from "../../context/TenantBrandingContext";
import { uploadUrl as _uploadUrl } from "../../lib/uploads";

const DS_CATEGORIES = [
  { v: "all", label: "All" },
  { v: "filter", label: "Filter" },
  { v: "membrane", label: "Membrane" },
  { v: "pump", label: "Pump" },
  { v: "chemical", label: "Chemical" },
  { v: "consumable", label: "Consumable" },
  { v: "tank", label: "Tank" },
  { v: "valve", label: "Valve" },
  { v: "sensor", label: "Sensor" },
  { v: "controller", label: "Controller" },
  { v: "vfd", label: "VFD" },
  { v: "fitting", label: "Fitting" },
  { v: "softener", label: "Softener" },
  { v: "uv_lamp", label: "UV Lamp" },
  { v: "ozone", label: "Ozone" },
  { v: "media", label: "Media" },
  { v: "component", label: "Component" },
  { v: "general", label: "General" },
];

function authedUploadUrl(url) {
  // [Iter 327] Delegate to smart `uploadUrl()` — accepts bare id,
  // path, and stale full URLs and always emits a fresh live URL.
  return _uploadUrl(url) || url || "";
}

/**
 * Wave 29 Chunk 8f — Dirk reported "safmag datasheet not opening" 2026-05-11.
 *
 * The legacy `target="_blank"` <a> with `href={authedUploadUrl(d.upload_url)}`
 * silently failed when `upload_url` was missing/null (just navigated to "").
 * This helper:
 *   1. Validates the URL before opening.
 *   2. Probes the upload endpoint with HEAD so a 404 (missing file) or 403
 *      (permission) surfaces as a toast instead of an empty browser tab.
 *   3. Opens in a new tab only on confirmed 200.
 */
async function openDatasheet(d) {
  // Wave 29 Chunk 8f.1 — Dirk 2026-05-11: "Safmag open PDF button now does
  // nothing". Root cause: the button was DISABLED when `upload_url` was
  // empty, so clicks were silently swallowed. Now we always allow the click;
  // we fall back to building the URL from `upload_id` and surface a toast
  // when no underlying file exists at all.
  let urlPath = d?.upload_url;
  if (!urlPath && d?.upload_id) {
    // Legacy / partial records — reconstruct the canonical path.
    urlPath = `/api/uploads/${d.upload_id}`;
  }
  if (!urlPath) {
    toast.error("No file attached to this datasheet. Re-upload the PDF to fix.");
    return;
  }
  const finalUrl = authedUploadUrl(urlPath);
  if (!finalUrl) {
    toast.error("Could not build a download URL.");
    return;
  }
  try {
    // HEAD probe surfaces 404 (missing storage) and 403 (no permission)
    // as toasts so the user is never dropped on a blank tab.
    const resp = await fetch(finalUrl, { method: "HEAD", credentials: "include" });
    if (resp.status === 404) {
      toast.error("Original file is missing from storage. Ask an admin to re-upload.");
      return;
    }
    if (resp.status === 401) {
      toast.error("Session expired — please refresh the page and log in again.");
      return;
    }
    if (resp.status === 403) {
      toast.error("You don't have access to this file.");
      return;
    }
    if (!resp.ok) {
      toast.error(`Could not open file (HTTP ${resp.status}). Try refreshing.`);
      return;
    }
  } catch (_e) {
    // Network errors fall through — let the browser try anyway, but log.
    // eslint-disable-next-line no-console
    console.warn("openDatasheet HEAD probe failed", _e);
  }
  window.open(finalUrl, "_blank", "noopener,noreferrer");
}

export default function DatasheetsTab({ inventory = [], suppliers = [], customers = [], isManager }) {
  const [datasheets, setDatasheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [manufacturer, setManufacturer] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (category !== "all") params.category = category;
      if (manufacturer) params.manufacturer = manufacturer;
      const r = await api.get("/datasheets", { params });
      setDatasheets(r.data?.items || []);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail, "Couldn't load datasheets"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, manufacturer]);

  // Search debounce
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const manufacturers = useMemo(() => {
    const set = new Set();
    datasheets.forEach((d) => d.manufacturer && set.add(d.manufacturer));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [datasheets]);

  const handleDelete = async (did) => {
    if (!window.confirm("Permanently delete this datasheet?")) return;
    try {
      await api.delete(`/datasheets/${did}`);
      toast.success("Datasheet removed.");
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail, "Delete failed"));
    }
  };

  return (
    <div className="space-y-4" data-testid="datasheets-tab">
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#647087]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search title, manufacturer, part #, tags…"
            className="hv-input hv-input-search"
            data-testid="datasheets-search"
          />
        </div>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="hv-input py-2 text-xs w-40"
          data-testid="datasheets-cat-filter"
        >
          {DS_CATEGORIES.map((c) => (
            <option key={c.v} value={c.v}>{c.label}</option>
          ))}
        </select>
        <select
          value={manufacturer}
          onChange={(e) => setManufacturer(e.target.value)}
          className="hv-input py-2 text-xs w-44"
          data-testid="datasheets-mfr-filter"
        >
          <option value="">All manufacturers</option>
          {manufacturers.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
        {isManager && (
          <button
            onClick={() => setShowUpload(true)}
            className="hv-btn hv-btn-primary ml-auto"
            data-testid="datasheets-upload-btn"
          >
            <Plus className="w-4 h-4" /> Upload datasheet
          </button>
        )}
      </div>

      {loading ? (
        <div className="hv-card p-12 text-center text-[#647087] text-sm">
          <Loader2 className="w-5 h-5 inline mr-2 animate-spin" /> Loading library…
        </div>
      ) : datasheets.length === 0 ? (
        <div className="hv-card p-12 text-center text-[#647087] text-sm">
          <FileText className="w-10 h-10 mx-auto mb-3 opacity-50" />
          <div className="text-base text-[#dde3ec] font-display mb-1">No datasheets yet</div>
          <div className="mb-4">Upload a PDF and Hidro will auto-extract manufacturer, part number, category and key specs.</div>
          {isManager && (
            <button
              onClick={() => setShowUpload(true)}
              className="hv-btn hv-btn-primary"
              data-testid="datasheets-upload-empty"
            >
              <Sparkles className="w-4 h-4" /> Upload your first datasheet
            </button>
          )}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {datasheets.map((d) => (
            <DatasheetCard
              key={d.id}
              d={d}
              isManager={isManager}
              onView={() => setViewing(d)}
              onEdit={() => setEditing(d)}
              onDelete={() => handleDelete(d.id)}
            />
          ))}
        </div>
      )}

      {showUpload && (
        <DatasheetUploadModal
          inventory={inventory}
          suppliers={suppliers}
          customers={customers}
          onClose={() => setShowUpload(false)}
          onSaved={() => {
            setShowUpload(false);
            load();
          }}
        />
      )}
      {editing && (
        <DatasheetEditModal
          datasheet={editing}
          inventory={inventory}
          suppliers={suppliers}
          customers={customers}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
      {viewing && (
        <DatasheetViewModal datasheet={viewing} onClose={() => setViewing(null)} />
      )}
    </div>
  );
}

function DatasheetCard({ d, isManager, onView, onEdit, onDelete }) {
  const linkedCount =
    (d.inventory_ids?.length || 0) +
    (d.supplier_ids?.length || 0) +
    (d.customer_ids?.length || 0);
  return (
    <div className="hv-card p-4 flex flex-col" data-testid={`datasheet-card-${d.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <button
            onClick={onView}
            className="font-display font-bold text-left hover:text-[#1fb6c1] transition w-full truncate"
            title={d.title}
            data-testid={`datasheet-title-${d.id}`}
          >
            {d.title || "Untitled datasheet"}
          </button>
          <div className="flex items-center gap-2 flex-wrap mt-0.5">
            {d.manufacturer && (
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border font-bold border-[#A78BFA]/40 text-[#A78BFA] bg-[#A78BFA]/10">
                {d.manufacturer}
              </span>
            )}
            {d.part_number && (
              <span className="text-[10px] font-mono text-[#1fb6c1] bg-[#1fb6c1]/10 border border-[#1fb6c1]/30 px-1.5 py-0.5 rounded">
                {d.part_number}
              </span>
            )}
            <span className="text-[10px] uppercase tracking-wider text-[#647087]">
              {d.category || "general"}
            </span>
          </div>
        </div>
        <FileText className="w-5 h-5 text-[#1fb6c1] flex-shrink-0" />
      </div>
      {d.summary && (
        <div className="text-[11px] text-[#a0aabf] mt-2 line-clamp-3 whitespace-pre-line">{d.summary}</div>
      )}
      {d.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {d.tags.slice(0, 6).map((t) => (
            <span key={t} className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/5 text-[#a0aabf] border border-white/10">
              {t}
            </span>
          ))}
        </div>
      )}
      <div className="flex items-center gap-3 mt-3 text-[10px] text-[#647087]">
        {(d.inventory_ids?.length || 0) > 0 && (
          <span className="inline-flex items-center gap-1"><Package className="w-3 h-3" /> {d.inventory_ids.length}</span>
        )}
        {(d.supplier_ids?.length || 0) > 0 && (
          <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3" /> {d.supplier_ids.length}</span>
        )}
        {(d.customer_ids?.length || 0) > 0 && (
          <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {d.customer_ids.length}</span>
        )}
        {linkedCount === 0 && <span className="text-[#647087]/70">Unlinked</span>}
        {d.pages && <span className="ml-auto">{d.pages}p</span>}
      </div>
      <div className="flex gap-2 mt-3">
        <button
          type="button"
          onClick={() => openDatasheet(d)}
          className="hv-btn hv-btn-ghost text-xs flex-1"
          data-testid={`datasheet-open-${d.id}`}
          title="Open the original PDF (toast will explain if the file is missing)"
        >
          <ExternalLink className="w-3 h-3" /> Open PDF
        </button>
        {isManager && (
          <>
            <button
              onClick={onEdit}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid={`datasheet-edit-${d.id}`}
            >
              <Edit3 className="w-3 h-3" />
            </button>
            <button
              onClick={onDelete}
              className="hv-btn hv-btn-ghost text-xs text-rose-300"
              data-testid={`datasheet-delete-${d.id}`}
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/* ---------------- Upload modal with auto-extract ---------------- */
function DatasheetUploadModal({ inventory, suppliers, customers, onClose, onSaved }) {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { branding } = useBranding();
  const aiName = branding?.ai_name || "Hidro";
  const [phase, setPhase] = useState("pick"); // pick | extracting | review
  const [file, setFile] = useState(null);
  const [extracted, setExtracted] = useState(null);
  const [upload, setUpload] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  const onPickFile = (f) => {
    if (!f) return;
    setFile(f);
    runExtract(f);
  };

  const runExtract = async (f) => {
    setPhase("extracting");
    const fd = new FormData();
    fd.append("file", f);
    try {
      const token = localStorage.getItem("hv_token");
      const resp = await fetch(`${API}/datasheets/extract`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!resp.ok) {
        const t = await resp.text();
        throw new Error(t || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      setExtracted(data.extracted);
      setUpload(data.upload);
      setForm({
        title: data.extracted?.title || f.name,
        manufacturer: data.extracted?.manufacturer || "",
        part_number: data.extracted?.part_number || "",
        category: data.extracted?.category || "general",
        summary: data.extracted?.summary || "",
        tags: (data.extracted?.tags || []).join(", "),
        key_specs: data.extracted?.key_specs || {},
        inventory_ids: [],
        supplier_ids: [],
        customer_ids: [],
      });
      setPhase("review");
      if (data.extracted?._ok === false) {
        toast.warning("Auto-extract returned partial data — please review fields.");
      } else {
        toast.success(`${aiName} extracted the metadata. Review and confirm.`);
      }
    } catch (err) {
      console.error(err);
      toast.error("Auto-extract failed. You can still fill in the fields manually.");
      setExtracted({ _ok: false });
      setUpload(null);
      setPhase("pick");
    }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!upload?.id) {
      toast.error("Upload missing — re-pick the file.");
      return;
    }
    setSaving(true);
    try {
      const tags = String(form.tags || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      const payload = {
        upload_id: upload.id,
        upload_url: upload.url,
        filename: upload.filename,
        content_type: upload.content_type,
        size_bytes: upload.size_bytes,
        pages: upload.pages || null,
        title: form.title,
        manufacturer: form.manufacturer || null,
        part_number: form.part_number || null,
        category: form.category,
        summary: form.summary,
        tags,
        key_specs: form.key_specs || {},
        inventory_ids: form.inventory_ids || [],
        supplier_ids: form.supplier_ids || [],
        customer_ids: form.customer_ids || [],
      };
      await api.post("/datasheets", payload);
      toast.success("Datasheet saved.");
      onSaved();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail, "Could not save"));
    } finally {
      setSaving(false);
    }
  };

  const title = phase === "extracting"
    ? `${aiName} is reading the document…`
    : phase === "review"
      ? "Confirm datasheet details"
      : "Upload datasheet";

  return (
    <Modal open onClose={onClose} title={title} testId="datasheet-upload-modal" maxWidth="max-w-3xl">
      {phase === "pick" && (
        <div
          className="border-2 border-dashed border-white/15 hover:border-[#1fb6c1]/50 rounded-md p-12 text-center cursor-pointer transition"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); }}
          onDrop={(e) => {
            e.preventDefault();
            const f = e.dataTransfer?.files?.[0];
            if (f) onPickFile(f);
          }}
          data-testid="datasheet-upload-dropzone"
        >
          <Upload className="w-10 h-10 mx-auto text-[#1fb6c1] mb-3" />
          <div className="font-display text-lg mb-1">Drop a PDF or image here</div>
          <div className="text-xs text-[#a0aabf] mb-4">
            Hidro will auto-extract manufacturer, part number, category, summary and key specs.
          </div>
          <button className="hv-btn hv-btn-primary" data-testid="datasheet-upload-pick">
            <FileText className="w-4 h-4" /> Choose file
          </button>
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,image/*"
            className="hidden"
            onChange={(e) => onPickFile(e.target.files?.[0])}
            data-testid="datasheet-file-input"
          />
        </div>
      )}

      {phase === "extracting" && (
        <div className="p-12 text-center">
          <Loader2 className="w-10 h-10 mx-auto text-[#1fb6c1] animate-spin mb-3" />
          <div className="font-display text-lg mb-1">Reading {file?.name}…</div>
          <div className="text-xs text-[#a0aabf]">
            Hidro is parsing the pages and extracting structured metadata.
          </div>
        </div>
      )}

      {phase === "review" && form && (
        <DatasheetForm
          form={form}
          setForm={setForm}
          extractedOk={extracted?._ok !== false}
          inventory={inventory}
          suppliers={suppliers}
          customers={customers}
          onSubmit={submit}
          submitLabel={saving ? "Saving…" : "Save datasheet"}
          submitting={saving}
        />
      )}
    </Modal>
  );
}

function DatasheetEditModal({ datasheet, inventory, suppliers, customers, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: datasheet.title || "",
    manufacturer: datasheet.manufacturer || "",
    part_number: datasheet.part_number || "",
    category: datasheet.category || "general",
    summary: datasheet.summary || "",
    tags: (datasheet.tags || []).join(", "),
    key_specs: datasheet.key_specs || {},
    inventory_ids: datasheet.inventory_ids || [],
    supplier_ids: datasheet.supplier_ids || [],
    customer_ids: datasheet.customer_ids || [],
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      const tags = String(form.tags || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      // Update metadata
      await api.patch(`/datasheets/${datasheet.id}`, {
        title: form.title,
        manufacturer: form.manufacturer || null,
        part_number: form.part_number || null,
        category: form.category,
        summary: form.summary,
        tags,
        key_specs: form.key_specs || {},
      });
      // Update links
      await api.post(`/datasheets/${datasheet.id}/link`, {
        inventory_ids: form.inventory_ids,
        supplier_ids: form.supplier_ids,
        customer_ids: form.customer_ids,
      });
      toast.success("Datasheet updated.");
      onSaved();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail, "Could not save"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={`Edit · ${datasheet.title}`} testId="datasheet-edit-modal" maxWidth="max-w-3xl">
      <DatasheetForm
        form={form}
        setForm={setForm}
        extractedOk
        inventory={inventory}
        suppliers={suppliers}
        customers={customers}
        onSubmit={submit}
        submitLabel={saving ? "Saving…" : "Save changes"}
        submitting={saving}
      />
    </Modal>
  );
}

function DatasheetForm({ form, setForm, extractedOk, inventory, suppliers, customers, onSubmit, submitLabel, submitting }) {
  return (
    <form onSubmit={onSubmit} className="space-y-3" data-testid="datasheet-form">
      {extractedOk && (
        <div className="text-[11px] text-emerald-300 inline-flex items-center gap-1.5 bg-emerald-500/10 border border-emerald-400/30 px-2 py-1 rounded">
          <Sparkles className="w-3 h-3" /> Hidro pre-filled the fields below — review and edit before saving.
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Title *</label>
          <input
            required
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="hv-input mt-1"
            data-testid="datasheet-input-title"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Manufacturer</label>
          <input
            value={form.manufacturer || ""}
            onChange={(e) => setForm({ ...form, manufacturer: e.target.value })}
            className="hv-input mt-1"
            data-testid="datasheet-input-manufacturer"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Part / Model #</label>
          <input
            value={form.part_number || ""}
            onChange={(e) => setForm({ ...form, part_number: e.target.value })}
            className="hv-input mt-1 font-mono text-xs"
            data-testid="datasheet-input-partnum"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Category</label>
          <select
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
            className="hv-input mt-1"
            data-testid="datasheet-input-category"
          >
            {DS_CATEGORIES.filter((c) => c.v !== "all").map((c) => (
              <option key={c.v} value={c.v}>{c.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Tags (comma-separated)</label>
          <input
            value={form.tags || ""}
            onChange={(e) => setForm({ ...form, tags: e.target.value })}
            placeholder="pump, stainless, multistage"
            className="hv-input mt-1"
            data-testid="datasheet-input-tags"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Summary</label>
          <VoiceTextarea
            value={form.summary || ""}
            onChange={(v) => setForm({...form, summary: v})}
            data-testid="datasheet-input-summary"
           testIdPrefix="datasheet-input-summary-voice" context="note" />
        </div>
      </div>

      {/* Key specs editor */}
      <div>
        <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Key specs</label>
        <KeySpecsEditor
          value={form.key_specs || {}}
          onChange={(v) => setForm({ ...form, key_specs: v })}
        />
      </div>

      {/* Linkers */}
      <div className="border border-white/10 rounded-md p-3 space-y-3 bg-white/[0.02]">
        <div className="text-[11px] uppercase tracking-wider text-[#a0aabf] flex items-center gap-1.5">
          <Tag className="w-3 h-3" /> Link this datasheet to (optional)
        </div>
        <MultiPicker
          label="Inventory items"
          icon={Package}
          options={inventory.map((i) => ({ id: i.id, label: i.name, sub: [i.sku, i.manufacturer].filter(Boolean).join(" · ") }))}
          selected={form.inventory_ids || []}
          onChange={(v) => setForm({ ...form, inventory_ids: v })}
          testId="datasheet-link-inventory"
        />
        <MultiPicker
          label="Suppliers"
          icon={Building2}
          options={(suppliers || []).map((s) => ({ id: s.id, label: s.name, sub: s.external_ref || "" }))}
          selected={form.supplier_ids || []}
          onChange={(v) => setForm({ ...form, supplier_ids: v })}
          testId="datasheet-link-suppliers"
        />
        <MultiPicker
          label="Customers"
          icon={Users}
          options={(customers || []).map((c) => ({ id: c.id, label: c.name, sub: c.external_ref || "" }))}
          selected={form.customer_ids || []}
          onChange={(v) => setForm({ ...form, customer_ids: v })}
          testId="datasheet-link-customers"
        />
      </div>

      <button
        type="submit"
        className="hv-btn hv-btn-primary w-full"
        disabled={submitting}
        data-testid="datasheet-submit"
      >
        <Check className="w-4 h-4" /> {submitLabel}
      </button>
    </form>
  );
}

function KeySpecsEditor({ value, onChange }) {
  const entries = Object.entries(value || {});
  const [newK, setNewK] = useState("");
  const [newV, setNewV] = useState("");
  const removeKey = (k) => {
    const next = { ...value };
    delete next[k];
    onChange(next);
  };
  const addKey = () => {
    if (!newK.trim()) return;
    onChange({ ...value, [newK.trim()]: newV.trim() });
    setNewK("");
    setNewV("");
  };
  const updateValue = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="space-y-1.5 mt-1" data-testid="datasheet-keyspecs">
      {entries.length === 0 && (
        <div className="text-[10px] text-[#647087]">No key specs yet — add the most useful ones for technicians.</div>
      )}
      {entries.map(([k, v]) => (
        <div key={k} className="flex items-center gap-2">
          <span className="text-[11px] font-mono text-[#1fb6c1] min-w-[140px] truncate" title={k}>{k}</span>
          <input
            value={v}
            onChange={(e) => updateValue(k, e.target.value)}
            className="hv-input flex-1 py-1 text-xs"
          />
          <button type="button" onClick={() => removeKey(k)} className="hv-btn hv-btn-ghost text-rose-300 px-2" title="Remove">
            <X className="w-3 h-3" />
          </button>
        </div>
      ))}
      <div className="flex items-center gap-2 pt-1.5 border-t border-white/5">
        <input
          value={newK}
          onChange={(e) => setNewK(e.target.value)}
          placeholder="spec name"
          className="hv-input py-1 text-xs min-w-[140px]"
        />
        <input
          value={newV}
          onChange={(e) => setNewV(e.target.value)}
          placeholder="value (with unit)"
          className="hv-input flex-1 py-1 text-xs"
        />
        <button type="button" onClick={addKey} className="hv-btn hv-btn-ghost text-xs" data-testid="datasheet-keyspec-add">
          <Plus className="w-3 h-3" /> Add
        </button>
      </div>
    </div>
  );
}

function MultiPicker({ label, icon: Icon, options, selected, onChange, testId }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const sel = new Set(selected);
  const filtered = q
    ? options.filter((o) => (o.label || "").toLowerCase().includes(q.toLowerCase()) || (o.sub || "").toLowerCase().includes(q.toLowerCase()))
    : options;
  const labelFor = (id) => options.find((o) => o.id === id)?.label || id;
  return (
    <div data-testid={testId}>
      <label className="text-[10px] uppercase tracking-wider text-[#647087] inline-flex items-center gap-1.5 mb-1">
        <Icon className="w-3 h-3" /> {label}
      </label>
      <div className="flex flex-wrap gap-1.5 mb-1">
        {selected.map((id) => (
          <span key={id} className="text-[10px] bg-[#1fb6c1]/15 text-[#1fb6c1] border border-[#1fb6c1]/30 px-2 py-0.5 rounded inline-flex items-center gap-1">
            {labelFor(id)}
            <button
              type="button"
              onClick={() => onChange(selected.filter((x) => x !== id))}
              className="hover:text-rose-300"
            >
              <X className="w-2.5 h-2.5" />
            </button>
          </span>
        ))}
        {selected.length === 0 && <span className="text-[10px] text-[#647087]">None linked</span>}
      </div>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="hv-btn hv-btn-ghost text-xs"
      >
        <Plus className="w-3 h-3" /> {open ? "Close picker" : `Add ${label.toLowerCase()}`}
      </button>
      {open && (
        <div className="border border-white/10 rounded-md mt-2 p-2 bg-[#0a0b0e]">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={`Search ${label.toLowerCase()}…`}
            className="hv-input py-1 text-xs mb-2"
          />
          <div className="max-h-40 overflow-y-auto space-y-0.5">
            {filtered.slice(0, 80).map((o) => {
              const active = sel.has(o.id);
              return (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => onChange(active ? selected.filter((x) => x !== o.id) : [...selected, o.id])}
                  className={`w-full text-left px-2 py-1 rounded text-xs transition ${active ? "bg-[#1fb6c1]/15 text-[#1fb6c1]" : "hover:bg-white/[0.04]"}`}
                >
                  <div className="truncate">{o.label}</div>
                  {o.sub && <div className="text-[10px] text-[#647087] truncate">{o.sub}</div>}
                </button>
              );
            })}
            {filtered.length === 0 && (
              <div className="text-[10px] text-[#647087] p-2 text-center">No matches.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function DatasheetViewModal({ datasheet, onClose }) {
  return (
    <Modal open onClose={onClose} title={datasheet.title} testId="datasheet-view-modal" maxWidth="max-w-4xl">
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2 items-center">
          {datasheet.manufacturer && (
            <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded border font-bold border-[#A78BFA]/40 text-[#A78BFA] bg-[#A78BFA]/10">
              {datasheet.manufacturer}
            </span>
          )}
          {datasheet.part_number && (
            <span className="text-xs font-mono text-[#1fb6c1] bg-[#1fb6c1]/10 border border-[#1fb6c1]/30 px-2 py-0.5 rounded">
              {datasheet.part_number}
            </span>
          )}
          <span className="text-[10px] uppercase tracking-wider text-[#647087]">{datasheet.category}</span>
          <button
            type="button"
            onClick={() => openDatasheet(datasheet)}
            className="hv-btn hv-btn-primary ml-auto text-xs"
            data-testid="datasheet-editor-open-pdf"
            title="Open the original PDF"
          >
            <ExternalLink className="w-3 h-3" /> Open original
          </button>
        </div>
        {datasheet.summary && (
          <p className="text-sm text-[#dde3ec] whitespace-pre-line">{datasheet.summary}</p>
        )}
        {datasheet.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {datasheet.tags.map((t) => (
              <span key={t} className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/5 text-[#a0aabf] border border-white/10">
                {t}
              </span>
            ))}
          </div>
        )}
        {Object.keys(datasheet.key_specs || {}).length > 0 && (
          <div className="border border-white/10 rounded-md p-3">
            <div className="text-[11px] uppercase tracking-wider text-[#a0aabf] mb-2">Key specs</div>
            <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
              {Object.entries(datasheet.key_specs).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-3 border-b border-white/5 py-1">
                  <span className="text-[#647087] uppercase tracking-wider text-[10px]">{k}</span>
                  <span className="text-[#dde3ec] font-mono text-right">{v}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <LinkedEntities datasheet={datasheet} />
      </div>
    </Modal>
  );
}

function LinkedEntities({ datasheet }) {
  const [linked, setLinked] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/datasheets/${datasheet.id}`);
        if (alive) setLinked(r.data);
      } catch (_) { /* noop */ }
    })();
    return () => { alive = false; };
  }, [datasheet.id]);
  if (!linked) return null;
  const blocks = [
    { items: linked.linked_inventory, label: "Inventory items", icon: Package },
    { items: linked.linked_suppliers, label: "Suppliers", icon: Building2 },
    { items: linked.linked_customers, label: "Customers", icon: Users },
  ].filter((b) => (b.items || []).length > 0);
  if (blocks.length === 0) return null;
  return (
    <div className="grid sm:grid-cols-3 gap-3">
      {blocks.map(({ items, label, icon: Icon }) => (
        <div key={label} className="border border-white/10 rounded-md p-3">
          <div className="text-[11px] uppercase tracking-wider text-[#a0aabf] mb-2 inline-flex items-center gap-1.5">
            <Icon className="w-3 h-3" /> {label}
          </div>
          <ul className="space-y-1 text-xs">
            {items.map((i) => (
              <li key={i.id} className="text-[#dde3ec] truncate">{i.name}{i.external_ref ? ` · ${i.external_ref}` : ""}</li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
